import { useQueryClient } from '@tanstack/react-query';
import {
  AiToolCallState,
  TreadAgentApiWriteType,
  TreadAgentSingleRecordWriteType,
} from '@treadinc/horizon-api-spec';
import { reaction } from 'mobx';
import { useEffect } from 'react';

import useCompanySettings from '~hooks/useCompanySettings/useCompanySettings';
import useUsers from '~hooks/useUsers/useUsers';
import {
  TREAD_AGENT_QUERY_KEY,
  TREAD_AGENT_SKILLS_QUERY_KEY,
} from '~queries/treadAgentQueryKeys';
import { useStores } from '~store';

// TreadAgentStore refreshes these itself on every resolved tool call
const AGENT_OWNED_QUERY_ROOTS: unknown[] = [
  TREAD_AGENT_QUERY_KEY,
  TREAD_AGENT_SKILLS_QUERY_KEY,
];

export default function useRefreshAfterTreadAgentWrites() {
  const { treadAgentStore, userStore } = useStores();
  const queryClient = useQueryClient();
  const { getUser } = useUsers();
  const { refreshCompanySettings } = useCompanySettings();

  useEffect(() => {
    let hasWritesToRefresh = false;

    // the agent can write any record, so every grid on screen refetches
    const refreshQueries = () => {
      hasWritesToRefresh = false;
      void queryClient.invalidateQueries({
        predicate: (query) => !AGENT_OWNED_QUERY_ROOTS.includes(query.queryKey[0]),
      });
    };

    const disposeToolCallReaction = reaction(
      () => treadAgentStore.lastResolvedToolCall,
      (toolCall) => {
        if (toolCall?.state !== AiToolCallState.APPLIED) {
          return;
        }

        const { preview, output } = toolCall;
        const userId = userStore.user.id;
        const companyId = userStore.userCompany.id;
        const isOwnUserWrite =
          (preview?.kind === TreadAgentSingleRecordWriteType.SINGLE_RECORD_WRITE &&
            preview.target.type === 'user' &&
            preview.target.id === userId) ||
          (Boolean(userId) && output?.id === userId);
        const isCompanySettingsWrite =
          preview?.kind === TreadAgentApiWriteType.API_WRITE &&
          preview.path === `/v1/companies/${companyId}/settings`;
        const writeTargetIsUnknown = !preview;

        // the user and company settings live in userStore, which no query refreshes
        if (isOwnUserWrite) {
          void getUser();
        } else if (isCompanySettingsWrite || writeTargetIsUnknown) {
          void refreshCompanySettings(companyId);
        }

        // a confirmed batch applies its writes one by one within a single turn, so the
        // queries refetch once when that turn ends instead of once per write
        if (treadAgentStore.isTurnInProgress) {
          hasWritesToRefresh = true;

          return;
        }

        refreshQueries();
      },
    );

    const disposeTurnReaction = reaction(
      () => treadAgentStore.isTurnInProgress,
      (isTurnInProgress) => {
        if (!isTurnInProgress && hasWritesToRefresh) {
          refreshQueries();
        }
      },
    );

    return () => {
      disposeToolCallReaction();
      disposeTurnReaction();
    };
  }, [getUser, queryClient, refreshCompanySettings, treadAgentStore, userStore]);
}
